<template>
  <section
    class="dashboard-upcoming"
    aria-labelledby="dashboard-upcoming-events-title"
  >
    <h2 id="dashboard-upcoming-events-title" class="dashboard-upcoming__title">
      {{ $t("upcoming_events") }}
    </h2>

    <ProgressBar
      v-if="isLoading"
      mode="indeterminate"
      class="dashboard-upcoming__loading"
    />

    <div v-else-if="events.length === 0" class="dashboard-upcoming__empty">
      <i
        class="pi pi-calendar dashboard-upcoming__empty-icon"
        aria-hidden="true"
      />
      <p class="dashboard-upcoming__empty-text">
        {{ $t("no_upcoming_events") }}
      </p>
    </div>

    <div v-else class="dashboard-upcoming__body">
      <ol class="event-timeline">
        <li
          v-for="event in events"
          :key="event.id"
          class="event-timeline__item"
        >
          <div class="event-timeline__marker" aria-hidden="true">
            <span class="event-timeline__dot" />
          </div>
          <div class="event-timeline__content">
            <time class="event-timeline__date">
              {{ formatSchedule(event) }}
            </time>
            <p class="event-timeline__name">{{ event.title }}</p>
            <p v-if="event.type === 'conference'" class="event-timeline__authors">
              {{ $t("conference") }}
              <template v-if="event.location || event.room">
                · {{ [event.location, event.room].filter(Boolean).join(" · ") }}
              </template>
            </p>
            <p v-else-if="event.authors" class="event-timeline__authors">
              {{ event.authors }}
            </p>
          </div>
        </li>
      </ol>
      <div class="dashboard-upcoming__actions">
        <router-link :to="{ name: 'eventos' }" class="dashboard-upcoming__link">
          {{ $t("events") }}
          <i class="pi pi-angle-right" aria-hidden="true" />
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import ProgressBar from "primevue/progressbar";

export default {
  name: "DashboardUpcomingEvents",
  components: { ProgressBar },
  data() {
    return {
      isLoading: false,
      events: [],
    };
  },
  created() {
    this.fetchList();
  },
  methods: {
    fetchList() {
      this.isLoading = true;
      this.$root
        .apiGet(this.$root.api.profileEvents, { params: { upcoming: true } })
        .then((response) => {
          this.events = response.data.data || [];
        })
        .catch((error) => {
          this.$root.toastError(
            (error &&
              error.response &&
              error.response.data &&
              error.response.data.msg_code) ||
              "error"
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    extractTime(value) {
      if (!value) {
        return null;
      }
      const raw = typeof value === "object" && value.date ? value.date : value;
      const match = String(raw).match(/[T ](\d{2}:\d{2})/);
      return match ? match[1] : null;
    },
    formatSchedule(event) {
      if (!event || !event.startDate) {
        return "";
      }
      const startDate = this.$root.formatDate(event.startDate);
      const endDate =
        event.endDate && this.$root.formatDate(event.endDate) !== startDate
          ? this.$root.formatDate(event.endDate)
          : null;
      const startTime = this.extractTime(event.startTime);
      const endTime = this.extractTime(event.endTime);

      let result = endDate ? `${startDate} - ${endDate}` : startDate;
      if (startTime) {
        result += ` · ${startTime}`;
        if (endTime && endTime !== startTime) {
          result += ` - ${endTime}`;
        }
      }
      return result;
    },
  },
};
</script>

<style scoped>
.event-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
}

.event-timeline__item {
  position: relative;
  display: flex;
  gap: 0.75rem;
  padding-bottom: 1.25rem;
}

.event-timeline__marker {
  position: relative;
  display: flex;
  justify-content: center;
  width: 14px;
  flex: 0 0 14px;
}

.event-timeline__marker::before {
  content: "";
  position: absolute;
  top: 6px;
  bottom: -6px;
  width: 2px;
  background: var(--surface-300, #dee2e6);
}

.event-timeline__item:last-child .event-timeline__marker::before {
  display: none;
}

.event-timeline__dot {
  position: relative;
  z-index: 1;
  width: 12px;
  height: 12px;
  margin-top: 4px;
  border-radius: 50%;
  background: var(--primary-color, #2563eb);
}

.event-timeline__content {
  flex: 1 1 auto;
}

.event-timeline__date {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--primary-color, #2563eb);
}

.event-timeline__name {
  margin: 0.15rem 0 0;
  font-weight: 600;
}

.event-timeline__authors {
  margin: 0.1rem 0 0;
  font-size: 0.85rem;
  color: var(--text-color-secondary, #6c757d);
}
</style>
